import { SectionCard } from "@/features/power_projects/components/ui";
import type { ZEElementType } from "../types";
import { elementSwatchSvg } from "../utils";

interface Props {
  elementTypes: ZEElementType[];
  activeTypeId: string | null;
  onSelect: (t: ZEElementType) => void;
  isFullscreen?: boolean;
}

/** The drawing palette: pick an element type, then draw it on the map. Lines and objects
 *  are grouped; the selected chip is highlighted. */
export function ZEElementPalette({ elementTypes, activeTypeId, onSelect, isFullscreen }: Props) {
  const lines = elementTypes.filter((t) => t.kind === "line");
  const objects = elementTypes.filter((t) => t.kind === "object");
  const areas = elementTypes.filter((t) => t.kind === "area");

  const chip = (t: ZEElementType) => {
    const active = t.id === activeTypeId;
    return (
      <button
        key={t.id}
        type="button"
        onClick={() => onSelect(t)}
        className={`flex items-center gap-1.5 px-2 py-1 border text-[11px] font-mono transition-colors ${
          active
            ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]"
            : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
        }`}
      >
        <span
          className="inline-flex items-center justify-center shrink-0 w-6"
          aria-hidden
          dangerouslySetInnerHTML={{ __html: elementSwatchSvg(t) }}
        />
        <span className="text-left">{t.label}</span>
        <span className="text-[var(--color-text-muted)]">· {t.unit}</span>
      </button>
    );
  };

  return (
    <SectionCard title="Paleta elementów" bodyClassName={isFullscreen ? "overflow-y-auto" : "max-h-[540px] overflow-y-auto"}>
      {elementTypes.length === 0 ? (
        <p className="text-[11px] font-mono text-amber-600">
          Brak typów elementów. Dodaj je w katalogu (ze-element-types).
        </p>
      ) : (
        <div className="space-y-3">
          {lines.length > 0 && (
            <div>
              <p className="section-label mb-1">Linie (trasa)</p>
              <div className="flex flex-wrap gap-1.5">{lines.map(chip)}</div>
            </div>
          )}
          {areas.length > 0 && (
            <div>
              <p className="section-label mb-1">Powierzchnie (odtworzenie)</p>
              <div className="flex flex-wrap gap-1.5">{areas.map(chip)}</div>
            </div>
          )}
          {objects.length > 0 && (
            <div>
              <p className="section-label mb-1">Obiekty (punkty)</p>
              <div className="flex flex-wrap gap-1.5">{objects.map(chip)}</div>
            </div>
          )}
          <p className="text-[10px] font-mono text-[var(--color-text-muted)] leading-snug">
            Wybierz typ i rysuj na mapie. Linię i powierzchnię kończysz dwuklikiem.
            Edycję/przesuwanie/usuwanie, parametry techniczne oraz zmianę typu
            narysowanego elementu włączasz ikonami na mapie.
          </p>
        </div>
      )}
    </SectionCard>
  );
}
